/* "Call me back" (27 Oct 2026, mock CallBack_1, signed off): name + mobile number, no login
   and no OTP - the Sramika team calls within 10 minutes (at 8 am at night) and books it for
   the customer. Under the Call buttons (car page, trip pages, city pages, drivers) and on
   the login page, for the visitors who stop there. The request goes to the Desk (backend
   controllers/callbackController.js), never to the owner. */

import { useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { getAuth } from "../utils/auth";
import { apiRequest } from "../utils/api";
import { track, visitSource } from "../utils/analytics";
import { fillText } from "../utils/leadDisplay";
import { CALL_NUMBER, prettyNumber } from "../utils/contactNumbers";
import { useLocalNames } from "../utils/localNames";

/** Night in India (9 pm - 8 am): the call comes at 8 am - the server decides the same way. */
const isNight = () => { const h = new Date(Date.now() + 5.5 * 3600000).getUTCHours(); return h >= 21 || h < 8; };
const placeText = (p) => String(p?.value || p || "").split(",")[0].trim();

/** The trip as one line for the team: "Vijayawada → Hyderabad · 28 Oct, 6:00 am". */
export function tripLabel({ fromLocation, toLocation, fromDateTime, label } = {}) {
  if (label) return label;
  const from = placeText(fromLocation), to = placeText(toLocation);
  const when = fromDateTime ? new Date(fromDateTime) : null;
  const whenText = when && !Number.isNaN(when.getTime())
    ? when.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "";
  return [from && to && from !== to ? `${from} → ${to}` : from || to, whenText].filter(Boolean).join(" · ");
}

export function CallBackSheet({ context = {}, onClose }) {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const me = user || getAuth() || {};
  const [name, setName] = useState(me.displayName || me.name || "");
  const [phone, setPhone] = useState(String(me.phone || "").replace(/\D/g, "").slice(-10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const trip = tripLabel(context);
  const night = isNight();
  // On screen in the reader's script (places, the car); the team still gets the English line.
  const ln = useLocalNames();
  const from = placeText(context.fromLocation), to = placeText(context.toLocation);
  const shown = lang === "en" || (!from && !to) ? [trip, context.carLabel].filter(Boolean).join(" · ")
    : [from && to && from !== to ? `${ln(from, "city")} → ${ln(to, "city")}` : ln(from || to, "city"), context.carLabel ? ln(context.carLabel, "car") : null].filter(Boolean).join(" · ");
  useEffect(() => { track("callback_opened", { from: context.page || null }); }, [context.page]);

  const send = async () => {
    const ph = phone.replace(/\D/g, "").slice(-10);
    if (name.trim().length < 2) { setErr(t("cbk_noName")); return; }
    if (!/^[6-9]\d{9}$/.test(ph)) { setErr(t("cbk_badPhone")); return; }
    setBusy(true); setErr("");
    try {
      const r = await apiRequest("/callback", { method: "POST", body: JSON.stringify({
        name: name.trim(), phone: ph, note, lang, page: context.page || null, source: visitSource(),
        carId: context.carId || null, carLabel: context.carLabel || null, driverId: context.driverId || null,
        trip: { from: placeText(context.fromLocation) || null, to: placeText(context.toLocation) || null, when: context.fromDateTime || null, type: context.tripType || null, label: trip || null },
      }) });
      track("callback_requested", { from: context.page || null });
      setDone(r?.data?.already ? "already" : r?.data?.night ? "night" : "day");
    } catch (e) {
      setErr(fillText(t("cbk_error"), { n: prettyNumber(CALL_NUMBER) }));
    } finally { setBusy(false); }
  };

  const field = "w-full h-[44px] rounded-xl border border-[#CBD5E1] bg-white px-3 text-[14px] text-[#0F172A] outline-none focus:border-[#7BAE00]";
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center" role="dialog" aria-modal="true" aria-label={t("cbk_title")}>
      <button type="button" aria-label={t("cbk_close")} onClick={onClose} className="absolute inset-0 bg-[rgba(15,23,42,0.45)]" />
      <div className="relative w-full max-w-md bg-white rounded-t-[22px] px-4 pt-4 pb-6 max-h-[92vh] overflow-y-auto">
        {done ? (
          <div className="text-center py-6">
            <p className="text-[40px]">✅</p>
            <p className="text-[16px] font-extrabold text-[#0F172A] mt-1">{t(done === "already" ? "cbk_already" : done === "night" ? "cbk_doneNight" : "cbk_done")}</p>
            <button type="button" onClick={onClose} className="mt-4 h-[44px] px-6 rounded-xl bg-[#7BAE00] text-white text-[14px] font-extrabold">{t("cbk_close")}</button>
          </div>
        ) : (
          <>
            <p className="text-[18px] font-extrabold text-[#0F172A]">{t("cbk_title")}</p>
            <p className="text-[12.5px] text-[#475569] mt-1 leading-snug">{t("cbk_sub")}</p>
            {night && <p className="text-[12px] font-bold text-[#B45309] mt-1.5">{t("cbk_night")}</p>}
            <label className="block text-[12px] font-bold text-[#64748B] mt-3 mb-1">{t("cbk_name")}</label>
            <input className={field} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            <label className="block text-[12px] font-bold text-[#64748B] mt-2.5 mb-1">{t("cbk_phone")}</label>
            <div className="flex items-center gap-2">
              <span className="h-[44px] px-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] flex items-center text-[14px] font-bold text-[#334155]">+91</span>
              <input className={field} value={phone} inputMode="numeric" maxLength={10} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} autoComplete="tel-national" />
            </div>
            {trip && (
              <>
                <label className="block text-[12px] font-bold text-[#64748B] mt-2.5 mb-1">{t("cbk_trip")}</label>
                <p className="min-h-[40px] rounded-xl bg-[#F1F5F9] px-3 py-2.5 text-[13px] text-[#334155]">{shown}</p>
              </>
            )}
            <label className="block text-[12px] font-bold text-[#64748B] mt-2.5 mb-1">{t("cbk_note")}</label>
            <input className={field} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder={t("cbk_notePh")} />
            {err && <p className="text-[12.5px] font-bold text-[#DC2626] mt-2">{err}</p>}
            <button type="button" disabled={busy} onClick={send}
              className="mt-4 w-full h-[48px] rounded-xl bg-[#7BAE00] text-white text-[15px] font-extrabold disabled:opacity-60 active:scale-[0.99]">
              {busy ? t("cbk_sending") : t("cbk_send")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** The outlined button under a Call button; opens the sheet with what the page knows. */
export function CallBackButton({ context = {}, className = "" }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className={`w-full min-h-[44px] rounded-2xl border-[1.5px] border-[#7BAE00] bg-white text-[#3F5E00] text-[13px] font-extrabold active:scale-[0.99] ${className}`}>
        {t("cbk_button")}
      </button>
      {open && <CallBackSheet context={context} onClose={() => setOpen(false)} />}
    </>
  );
}
